import { ReactNode, useCallback, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router-dom';
import { steps, useCookieValues, useUserFlags } from '~/hooks';
import { AnalyticsContext } from './AnalyticsContext';

export const eventName = 'dbmixedIncomeBuilder';

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { insights, standardLifeCookie } = useCookieValues();
    const [insightsInitialised, setInsightsInitialised] = useState<boolean>(false);

    const { pathname } = useLocation();
    const paths = ['/', ...steps];
    const pathIndex = paths.indexOf(pathname);

    const { getFlag } = useUserFlags();
    const isMobileApp = getFlag('isMobileApp');

    useEffect(() => {
        if (!insightsInitialised && insights) {
            TagManager.initialize({
                gtmId: import.meta.env.VITE_EXPERIENCE_AND_INSIGHTS_GTM_CODE,
                events: {
                    [eventName]: eventName,
                },
            });
            setInsightsInitialised(true);
            return;
        } else if (insightsInitialised && !insights) {
            setInsightsInitialised(false);
        }
    }, [insights, insightsInitialised, setInsightsInitialised]);

    const pushEvent = useCallback(
        (data: Record<string, string>) => {
            if (insightsInitialised) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: eventName,
                        eventAction: `Step ${pathIndex}`,
                        eventCategory: `${pathname} | ${isMobileApp ? 'mobile' : 'dashboard'}`,
                        ...data,
                    },
                });
            }
        },
        [insightsInitialised, pathname]
    );

    useEffect(() => {
        pushEvent({ eventLabel: 'Page load' });
    }, [insightsInitialised, pathname]);

    return <AnalyticsContext.Provider value={{ pushEvent, standardLifeCookie }}>{children}</AnalyticsContext.Provider>;
};
